if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      lastVariantId = null;

      // ─── LIFECYCLE ────────────────────────────────────────────────────────────

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');

        // Media element lookup for instant active-slide switching.
        this.mediaElements = new Map();
        this.querySelectorAll('.product__media-item[data-media-id]').forEach(item => {
          const mediaId = item.dataset.mediaId.split('-').pop();
          this.mediaElements.set(mediaId, item);
        });

        // ── IN-MEMORY CACHE (this product only) ──────────────────────────────
        // Plain Maps living on this element. No IndexedDB, no sessionStorage,
        // nothing persisted. Automatically freed when the customer leaves this
        // product (element removed / page reloaded), so nothing accumulates and
        // storage can never fill up. A market change is a full page reload, so
        // these start empty in the new market — no stale price/stock possible.
        this.variantCache = new Map();  // variantId -> full product-info section HTML
        this.featureCache = new Map();  // variantId -> feature slider section HTML
        this.blocksCache  = new Map();  // variantId -> variant image blocks section HTML

        // Full variant list — used to resolve variant IDs and to prefetch.
        this.allVariants = [];
        try {
          const script = this.querySelector('[data-product-variants]');
          if (script) this.allVariants = JSON.parse(script.textContent);
        } catch (e) {}
        this.optionValueMap = null;

        try {
          const selectedScript = this.querySelector('[data-selected-variant]');
          this.lastVariantId = selectedScript ? JSON.parse(selectedScript.textContent)?.id ?? null : null;
        } catch (e) {
          this.lastVariantId = null;
        }
      }

      // ─── CACHE HELPERS (synchronous, in-memory) ───────────────────────────────

      getVariantOptionMap() {
        const map = new Map();
        this.querySelectorAll('[data-option-value-id]').forEach((el) => {
          map.set(el.dataset.optionValueId, el.value || el.getAttribute('value'));
        });
        return map;
      }

      resolveVariantId(selectedOptionValues) {
        if (!this.allVariants?.length || !selectedOptionValues?.length) return null;
        const map = this.optionValueMap || (this.optionValueMap = this.getVariantOptionMap());
        const values = selectedOptionValues.map((id) => map.get(id)).filter(Boolean);
        if (values.length !== selectedOptionValues.length) return null;
        const match = this.allVariants.find(
          (v) => v.options.length === values.length && v.options.every((o, i) => o === values[i])
        );
        return match ? match.id : null;
      }

      // ─── BACKGROUND PREFETCH (current product only) ───────────────────────────
      // After page load, quietly fetch each variant's section HTML into the
      // in-memory cache so switching is instant. Throttled to 3 at a time.

      schedulePrefetch() {
        if (!this.allVariants.length) return;

        // Warm the browser's image cache too (cheap, inert).
        this.allVariants.forEach((variant) => {
          const src = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
          if (src) {
            const img = new Image();
            img.src = src.includes('?') ? `${src}&width=1200` : `${src}?width=1200`;
          }
        });

        const start = () => {
          const queue = [...this.allVariants];
          const CONCURRENCY = 3;

          const next = () => {
            if (!queue.length) return;
            const variant = queue.shift();
            if (this.variantCache.has(variant.id)) { next(); return; }
            fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`)
              .then((r) => r.text())
              .then((text) => { this.variantCache.set(variant.id, text); })
              .catch(() => {})
              .finally(() => { next(); });
          };

          for (let i = 0; i < CONCURRENCY; i++) next();
        };

        if (document.readyState === 'complete') start();
        else window.addEventListener('load', start, { once: true });
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        this.schedulePrefetch();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        // Explicitly drop references so memory is freed promptly on navigation.
        this.variantCache?.clear();
        this.featureCache?.clear();
        this.blocksCache?.clear();
      }

      // ─── INIT ─────────────────────────────────────────────────────────────────

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      // ─── VARIANT CHANGE ENTRY POINT ───────────────────────────────────────────

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // In-memory cache hit → instant, synchronous, no network. Safe because
        // this cache only holds the current product in the current market and
        // is thrown away the moment the customer leaves.
        if (!shouldSwapProduct) {
          const resolvedId = this.resolveVariantId(selectedOptionValues);
          const cached = resolvedId ? this.variantCache.get(resolvedId) : null;
          if (cached) {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(cached, 'text/html');
            this.handleUpdateProductInfo(productUrl)(html);
            document.querySelector(`#${target.id}`)?.focus();
            return;
          }
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          cacheable: !shouldSwapProduct,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      // ─── SECTION RENDERING API — MAIN PRODUCT ─────────────────────────────────

      renderProductInfo({ requestUrl, targetId, callback, cacheable = false }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (cacheable) {
              const variant = this.getSelectedVariant(html);
              if (variant) this.variantCache.set(variant.id, responseText);
            }
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);
            }
          });
      }

      // ─── PRODUCT SWAP (different product) ────────────────────────────────────

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      // ─── VARIANT UPDATE (same product) ───────────────────────────────────────

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          if (variant?.id === this.lastVariantId) return;
          this.lastVariantId = variant?.id;

          this.updateAllInstant(variant, productUrl, html);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          requestAnimationFrame(() => {
            this.updateMedia(html, variant.featured_media?.id);
            this.pickupAvailability?.update(variant);
            this.updateOptionValues(html);
            this.updateQuantityRules(this.sectionId, html);
            publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.sectionId, html, variant } });
            this.updateFeatureSection(variant);
            this.updateVariantBlocks(variant);
          });
        };
      }

      // ─── PHASE 1: INSTANT UPDATES ─────────────────────────────────────────────

      updateAllInstant(variant, productUrl, html) {
        const variantId = variant?.id;
        const featuredMediaId = variant?.featured_media?.id;

        this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((form) => {
            const input = form.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });

        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${productUrl}${variantId ? `?variant=${variantId}` : ''}`
        );
        if (this.dataset.updateUrl !== 'false') {
          window.history.replaceState({}, '', `${productUrl}${variantId ? `?variant=${variantId}` : ''}`);
        }

        if (featuredMediaId) {
          const targetMedia = this.mediaElements.get(featuredMediaId.toString());
          if (targetMedia) {
            this.querySelectorAll('.product__media-item.is-active').forEach(item => item.classList.remove('is-active'));
            targetMedia.classList.add('is-active');
            this.querySelector('media-gallery')?.setActiveMedia?.(`${this.dataset.section}-${featuredMediaId}`, true);

            [
              this.querySelector('slider-component .slider'),
              this.querySelector('scroll-carousel'),
              this.querySelector('media-gallery .slider'),
              this.querySelector('.product__media-list'),
            ].filter(Boolean).forEach(slider => {
              slider.scrollLeft = 0;
              slider.scrollTo?.({ left: 0, behavior: 'smooth' });
            });

            const counter = this.querySelector('.slider-counter--current');
            if (counter) counter.textContent = '1';

            this.querySelectorAll('.slider__pagination button, carousel-navigation button').forEach((dot, index) => {
              if (index === 0) {
                dot.setAttribute('aria-current', 'true');
                dot.classList.add('is-active', 'active');
              } else {
                dot.removeAttribute('aria-current');
                dot.classList.remove('is-active', 'active');
              }
            });

            this.querySelectorAll('[style*="transform"]').forEach(slider => {
              if (slider.style.transform?.includes('translate')) {
                slider.style.transform = slider.style.transform.replace(/translateX?\([^)]*\)/g, 'translateX(0px)');
              }
            });
          }
        }

        [
          ['price'],
          ['Sku',           s => s.classList.contains('hidden')],
          ['Inventory',     s => s.innerText === ''],
          ['Volume'],
          ['Price-Per-Item', s => s.classList.contains('hidden')],
          ['Installments'], // NOBL-002: instalment banner follows the selected variant
        ].forEach(([id, shouldHide]) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const dest = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && dest) {
            dest.innerHTML = source.innerHTML;
            shouldHide && dest.classList.toggle('hidden', shouldHide(source));
          }
        });

        const isButtonDisabled = html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled');
        this.productForm?.toggleSubmitButton(isButtonDisabled, isButtonDisabled ? window.variantStrings.soldOut : null);

        ['Quantity-Rules', 'Volume-Note'].forEach(id =>
          this.querySelector(`#${id}-${this.dataset.section}`)?.classList.remove('hidden')
        );
      }

      // ─── PHASE 2: DEFERRED — PRODUCT GALLERY ─────────────────────────────────

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        const source = this.querySelector('media-gallery ul');
        const dest = html.querySelector('media-gallery ul');
        if (!source || !dest) return;

        const sourceIds = [...source.children].map(item => item.dataset.mediaId);
        const destIds = [...dest.children].map(item => item.dataset.mediaId);

        if (JSON.stringify(sourceIds) !== JSON.stringify(destIds)) {
          const fragment = document.createDocumentFragment();
          [...dest.children].forEach(destItem => {
            const mediaId = destItem.dataset.mediaId;
            const existingItem = source.querySelector(`[data-media-id="${mediaId}"]`);
            fragment.appendChild(existingItem || destItem.cloneNode(true));
          });
          source.replaceChildren(fragment);

          this.mediaElements.clear();
          this.querySelectorAll('.product__media-item[data-media-id]').forEach(item => {
            const mediaId = item.dataset.mediaId.split('-').pop();
            this.mediaElements.set(mediaId, item);
          });

          setTimeout(() => {
            const slider = this.querySelector('.slider, .product__media-list, scroll-carousel');
            if (slider) slider.scrollLeft = 0;
          }, 50);
        }

        const modalContent = this.productModal?.querySelector('.product-media-modal__content');
        const newModalContent = html.querySelector('product-modal .product-media-modal__content');
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      // ─── PHASE 2: DEFERRED — FEATURE SLIDER (in-memory cached) ────────────────

      updateFeatureSection(variant) {
        const featureSections = document.querySelectorAll('.product-features-slider');
        if (!featureSections.length) return;

        const sectionId = featureSections[0].dataset.sectionId;
        const productUrl = this.dataset.url;

        const render = (responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newActiveSection = html.querySelector('.product-features-slider--active');
          if (!newActiveSection) return;

          const targetSection = document.querySelector(
            `.product-features-slider[data-variant-id="${newActiveSection.dataset.variantId}"]`
          );

          if (targetSection) {
            targetSection.innerHTML = newActiveSection.innerHTML;
            targetSection.style.display = 'block';
            targetSection.classList.add('product-features-slider--active');
          } else {
            newActiveSection.style.display = 'block';
            const currentActive = document.querySelector('.product-features-slider');
            if (currentActive) {
              currentActive.parentNode.replaceChild(newActiveSection, currentActive);
            }
          }

          if (typeof cleanupSliders === 'function') cleanupSliders();
          setTimeout(() => {
            if (typeof initializeSliders === 'function') initializeSliders();
          }, 100);
        };

        const cached = this.featureCache.get(variant.id);
        if (cached) { render(cached); return; }

        fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            this.featureCache.set(variant.id, responseText);
            render(responseText);
          })
          .catch((e) => console.error('Feature section update failed:', e));
      }

      // ─── PHASE 2: DEFERRED — VARIANT IMAGE BLOCKS (in-memory cached) ──────────

      updateVariantBlocks(variant) {
        const wrapper = document.querySelector('.dynamic-variant-blocks[data-section-id]');
        if (!wrapper) return;

        const sectionId = wrapper.dataset.sectionId;
        const productUrl = this.dataset.url;

        const render = (responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newActiveItem = html.querySelector('.dynamic-variant-blocks__item');
          if (!newActiveItem) return;
          const currentItem = wrapper.querySelector('.dynamic-variant-blocks__item');
          currentItem?.remove();
          wrapper.querySelector('.dynamic-variant-blocks__content')?.appendChild(newActiveItem);
        };

        const cached = this.blocksCache.get(variant.id);
        if (cached) { render(cached); return; }

        fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            this.blocksCache.set(variant.id, responseText);
            render(responseText);
          })
          .catch((e) => console.error('Variant blocks update failed:', e));
      }

      // ─── UNAVAILABLE STATE ────────────────────────────────────────────────────

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .forEach((id) => this.querySelector(`#${id}-${this.dataset.section}`)?.classList.add('hidden'));
      }

      // ─── QUANTITY RULES ───────────────────────────────────────────────────────

      setQuantityBoundries() {
        if (!this.quantityInput) return;
        const data = {
          cartQuantity: parseInt(this.quantityInput.dataset.cartQuantity) || 0,
          min:          parseInt(this.quantityInput.dataset.min) || 1,
          max:          parseInt(this.quantityInput.dataset.max) || null,
          step:         parseInt(this.quantityInput.step) || 1,
        };
        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        this.quantityInput.min = min;
        max ? this.quantityInput.max = max : this.quantityInput.removeAttribute('max');
        this.quantityInput.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        ['.quantity__input', '.quantity__rules', '.quantity__label'].forEach(selector => {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) return;

          if (selector === '.quantity__input') {
            ['data-cart-quantity', 'data-min', 'data-max', 'step'].forEach(attr => {
              const value = updated.getAttribute(attr);
              value !== null ? current.setAttribute(attr, value) : current.removeAttribute(attr);
            });
          } else {
            current.innerHTML = updated.innerHTML;
          }
        });
      }

      // ─── HELPERS ──────────────────────────────────────────────────────────────

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }
        return `${url}?${params.join('&')}`;
      }

      // ─── GETTERS ──────────────────────────────────────────────────────────────

      get productForm() { return this.querySelector('product-form'); }
      get productModal() { return document.querySelector(`#ProductModal-${this.dataset.section}`); }
      get pickupAvailability() { return this.querySelector('pickup-availability'); }
      get variantSelectors() { return this.querySelector('variant-selects'); }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products');
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'quick_order_list');
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() { return this.dataset.originalSection || this.dataset.section; }
    }
  );
}